/**
 * Reads the session hint left by the server at login, and picks the page to render.
 * The real role lives on the session document; the authRole cookie only saves a round
 * trip here. Editing it gains nothing - the server never reads it.
 */
import Constants from "./Constants";
import rolePages from "../public/javascripts/rolePages";

// Where the reason waits while the browser navigates
const DENIAL_STORAGE_KEY = "dcat_access_denied_reason";

function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2)
        return parts.pop().split(';').shift();
    return undefined;
}

// The role of the current session, or undefined when not logged in
function getRole() {
    const role = getCookie(Constants.ROLE_COOKIE);
    return role || undefined;
}

function isLoggedIn() {
    return getRole() !== undefined;
}

function hasAdminPriv() {
    return rolePages.mayUseAdminInterface(getRole());
}

/**
 * Where a session belongs after logging in from a submission page.
 * @param adminPath This page's admin interface, "admin" or "appealsAdmin"
 * @returns {string|undefined} Where to go, or undefined to stay put
 */
function landingPathFor(role, schoolName, adminPath) {
    if (rolePages.mayUseAdminInterface(role))
        return rolePages.pathTo(schoolName, adminPath);
    if (role === rolePages.roles.JUDGE)
        return rolePages.pathTo(schoolName, rolePages.pages.CASE_LOOKUP);
    // Users belong right here, on the submission form
    return undefined;
}

/**
 * Leaves a page this session may not use. The reason is stashed rather than shown - a
 *  notification on a page about to be replaced is no notification.
 */
function redirectAway(redirect) {
    rememberDenial(redirect.reason);
    window.location.replace(redirect.path);
}

function rememberDenial(reason) {
    try {
        // First reason wins: an in flight 401 must not replace it with a vaguer one
        if (window.sessionStorage.getItem(DENIAL_STORAGE_KEY))
            return;
        window.sessionStorage.setItem(DENIAL_STORAGE_KEY, reason);
    } catch (e) {
        // Private modes can refuse sessionStorage. The redirect matters more.
    }
}

/**
 * Reads and clears the reason we were sent here. Cleared so a reload does not repeat it.
 */
function takeDenialReason() {
    try {
        const reason = window.sessionStorage.getItem(DENIAL_STORAGE_KEY);
        if (reason)
            window.sessionStorage.removeItem(DENIAL_STORAGE_KEY);
        return reason || undefined;
    } catch (e) {
        return undefined;
    }
}

function denialMessage(reason) {
    if (!reason) return undefined;
    return Constants.accessDenied[reason] || Constants.accessDenied.default;
}

export default {
    roles: rolePages.roles,
    pages: rolePages.pages,
    denialReasons: rolePages.denialReasons,
    getRole,
    isLoggedIn,
    hasAdminPriv,
    landingPathFor,
    adminPageRedirect: rolePages.adminPageRedirect,
    appealsAdminPageRedirect: rolePages.appealsAdminPageRedirect,
    homePathFor: rolePages.homePathFor,
    redirectAway,
    takeDenialReason,
    denialMessage,
};
